import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Box,
  Badge,
} from "@mantine/core";
import { IconFileText } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { esignatureService } from "../../services/esignature";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import DocumentViewerModal from "./DocumentViewerModal";
import "./RecruitingDashboard.css";

const getRecipientDisplayName = recipient => {
  if (!recipient) return "";
  if (recipient.full_name) return recipient.full_name;
  if (recipient.name) return recipient.name;
  if (recipient.first_name || recipient.last_name) {
    return `${recipient.first_name || ""} ${recipient.last_name || ""}`.trim();
  }
  if (recipient.lead) {
    if (recipient.lead.first_name || recipient.lead.last_name) {
      return `${recipient.lead.first_name || ""} ${recipient.lead.last_name || ""}`.trim();
    }
    if (recipient.lead.name) return recipient.lead.name;
  }
  if (recipient.user) {
    if (recipient.user.first_name || recipient.user.last_name) {
      return `${recipient.user.first_name || ""} ${recipient.user.last_name || ""}`.trim();
    }
    if (recipient.user.name) return recipient.user.name;
  }
  return recipient.email || "Unknown";
};

const formatDate = dateString => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function RecruitingDocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT } = useAuth();

  const [documentStats, setDocumentStats] = useState(null);
  const [loadingDocStats, setLoadingDocStats] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docFilterStatus, setDocFilterStatus] = useState("all");
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const recruitingTabs = [
    { label: "Dashboard", path: "/recruiting" },
    { label: "Pipeline", path: "/recruiting/pipeline" },
    { label: "Lists", path: "/recruiting/list" },
    { label: "Documents", path: "/recruiting/documents" },
  ];

  const fetchDocumentStats = async () => {
    try {
      setLoadingDocStats(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/esignature/documents/stats/summary"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setDocumentStats(result.data || null);
      } else {
        setDocumentStats(null);
      }
    } catch (error) {
      setDocumentStats(null);
    } finally {
      setLoadingDocStats(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const jwt = await getVerifiedJWT();
      const status = docFilterStatus === "all" ? null : docFilterStatus;
      const result = await esignatureService.getDocuments(jwt, status);

      let documentsArray = [];
      if (Array.isArray(result)) {
        documentsArray = result;
      } else if (result && Array.isArray(result.data)) {
        documentsArray = result.data;
      } else if (result && Array.isArray(result.documents)) {
        documentsArray = result.documents;
      } else if (
        result &&
        result.data &&
        Array.isArray(result.data.documents)
      ) {
        documentsArray = result.data.documents;
      }

      setDocuments(documentsArray);
    } catch (error) {
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchDocumentStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFilterStatus]);

  return (
    <PageContent
      customHeader={
        <div className="recruiting-page-custom-header">
          <div className="recruiting-page-header-left">
            <h1 className="recruiting-page-main-title">Recruiting</h1>
            <div className="recruiting-page-nav-tabs">
              {recruitingTabs.map(tab => (
                <button
                  key={tab.path}
                  className={`recruiting-page-nav-tab ${
                    location.pathname === tab.path ? "active" : ""
                  }`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={3}>Documents</Title>
            <Text c="dimmed" size="sm">
              E-Signature document states
            </Text>
          </div>
          <Button
            leftSection={<IconFileText size={14} />}
            onClick={() => navigate("/esignature/templates")}
            variant="default"
            size="sm"
          >
            View All Templates
          </Button>
        </Group>

        {loadingDocStats ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Loading document statistics...
            </Text>
          </Group>
        ) : documentStats ? (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
            <Paper withBorder p="lg" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Draft
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.draft || 0}
              </Text>
              <Badge color="gray" variant="light" size="sm" mt={8}>
                In Progress
              </Badge>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Sent
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.sent || 0}
              </Text>
              <Badge color="blue" variant="light" size="sm" mt={8}>
                Awaiting Signatures
              </Badge>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Completed
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.completed || 0}
              </Text>
              <Badge color="green" variant="light" size="sm" mt={8}>
                Done
              </Badge>
            </Paper>
          </SimpleGrid>
        ) : null}

        <div className="doc-filter-tabs" style={{ marginBottom: "1rem" }}>
          {["all", "draft", "sent", "completed"].map(status => (
            <button
              key={status}
              className={`doc-filter-tab${docFilterStatus === status ? " active" : ""}`}
              onClick={() => setDocFilterStatus(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {loadingDocs ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Loading documents...
            </Text>
          </Group>
        ) : documents.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" fs="italic">
            No documents found
          </Text>
        ) : (
          <Box>
            <Box
              style={{
                background: "white",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 1.2fr 1fr 1fr",
                  gap: "12px",
                  padding: "12px 16px",
                  background: "#f8f9fa",
                  borderBottom: "2px solid #e0e0e0",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#495057",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                <div>Document Title</div>
                <div>Direct Seller</div>
                <div>Guarantor</div>
                <div style={{ textAlign: "center" }}>Status</div>
                <div>Date Sent</div>
              </Box>

              <Stack gap={0}>
                {documents.slice(0, 8).map((doc, index) => {
                  const directSeller = doc.recipients?.find(
                    r => r.recipient_type === "direct_seller"
                  );
                  const guarantor = doc.recipients?.find(
                    r => r.recipient_type === "guarantor"
                  );

                  return (
                    <Box
                      key={doc.id}
                      onClick={() => {
                        setSelectedDocument(doc);
                        setShowViewerModal(true);
                      }}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1.2fr 1.2fr 1fr 1fr",
                        gap: "12px",
                        padding: "14px 16px",
                        borderBottom:
                          index < documents.slice(0, 8).length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                        <IconFileText
                          size={18}
                          style={{ color: "#1e3a8a", flexShrink: 0 }}
                        />
                        <Text size="sm" fw={500} lineClamp={1}>
                          {doc.title}
                        </Text>
                      </Group>
                      <div style={{ minWidth: 0 }}>
                        <Text size="xs" fw={500} lineClamp={1}>
                          {getRecipientDisplayName(directSeller)}
                        </Text>
                        <Text size="10px" c="dimmed" lineClamp={1}>
                          {directSeller?.email || "—"}
                        </Text>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text size="xs" fw={500} lineClamp={1}>
                          {getRecipientDisplayName(guarantor)}
                        </Text>
                        <Text size="10px" c="dimmed" lineClamp={1}>
                          {guarantor?.email || "—"}
                        </Text>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <Badge
                          size="sm"
                          variant="light"
                          color={
                            doc.status === "completed"
                              ? "green"
                              : doc.status === "sent"
                                ? "blue"
                                : "gray"
                          }
                        >
                          {doc.status === "in_progress"
                            ? "In Progress"
                            : doc.status}
                        </Badge>
                      </div>
                      <Text size="xs" c="dimmed">
                        {doc.sent_at ? formatDate(doc.sent_at) : "—"}
                      </Text>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}
      </Stack>

      {showViewerModal && selectedDocument && (
        <DocumentViewerModal
          isOpen={showViewerModal}
          onClose={() => {
            setShowViewerModal(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
        />
      )}
    </PageContent>
  );
}

export default RecruitingDocumentsPage;
